import React, { useState } from 'react';
import { Linkedin, Twitter, Facebook, Globe, HelpCircle } from 'lucide-react';
import { OrganizationDetail } from '../../types/interfaces';
import { useTranslation } from 'react-i18next';

import defaultDamelioLogo from '../../assets/damelio_logo_negro.png';
import unknownLogo from '../../assets/unknown_logo.png';

interface OrganizationDetailsProps {
  organization: OrganizationDetail;
}

const OrganizationDetails: React.FC<OrganizationDetailsProps> = ({ organization }) => {
  const { t } = useTranslation();
  const [showTooltip, setShowTooltip] = useState(false);

  // ✨ Función de utilidad para el fallback del logo
  function getFinalLogo(url?: string | null): string {
    // 1) Si la propiedad es "damelio", usar el logo local de Damelio
    if (url === 'damelio') {
      return defaultDamelioLogo;
    }
    // 2) Si está vacío, null o undefined, usar 'unknownLogo'
    if (!url || url.trim() === '') {
      return unknownLogo;
    }
    // 3) En cualquier otro caso, usar la URL remota tal cual
    return url;
  }

  const {
    name,
    clean_description,
    website_url,
    linkedin_url,
    twitter_url,
    facebook_url,
    phone,
    alexa_ranking,
    founded_year,
    logo_url,
    estimated_num_employees,
    street_address,
    city,
    state,
    country,
    postal_code
  } = organization;

  // ✨ Calcula el logo final
  const finalLogoUrl = getFinalLogo(logo_url);

  return (
    <div
      className="bg-white p-6 rounded-lg shadow-lg mb-6 mx-auto max-w-4xl"
      style={{ boxShadow: '0px 4px 10px rgba(169, 169, 169, 0.6)' }}
    >
      {/* Logo y Nombre centrados */}
      {/* ✨ Usamos 'finalLogoUrl' en vez de 'logo_url' */}
      <img
        src={finalLogoUrl}
        alt={name || t('organization.default_alt', 'Organización')}
        className="mx-auto w-32 mb-4"
      />

      <h2 className="text-3xl font-bold text-center mb-2">{name}</h2>

      {/* Descripción centrada */}
      {clean_description && (
        <p className="text-gray-700 text-justify mb-6 max-w-2xl mx-auto">
          {clean_description}
        </p>
      )}

      {/* Iconos de redes sociales centrados */}
      <div className="flex justify-center space-x-4 mb-6">
        {website_url && (
          <a
            href={website_url}
            target="_blank"
            rel="noopener noreferrer"
            className="text-blue-500 hover:text-blue-700"
          >
            <Globe className="w-6 h-6" />
          </a>
        )}
        {linkedin_url && (
          <a
            href={linkedin_url}
            target="_blank"
            rel="noopener noreferrer"
            className="text-blue-500 hover:text-blue-700"
          >
            <Linkedin className="w-6 h-6" />
          </a>
        )}
        {twitter_url && (
          <a
            href={twitter_url}
            target="_blank"
            rel="noopener noreferrer"
            className="text-blue-500 hover:text-blue-700"
          >
            <Twitter className="w-6 h-6" />
          </a>
        )}
        {facebook_url && (
          <a
            href={facebook_url}
            target="_blank"
            rel="noopener noreferrer"
            className="text-blue-500 hover:text-blue-700"
          >
            <Facebook className="w-6 h-6" />
          </a>
        )}
      </div>

      {/* Información adicional de la organización */}
      <div className="text-left space-y-2 max-w-2xl mx-auto">
        {phone && (
          <p>
            <strong>{t('organization.phone')}:</strong> {phone}
          </p>
        )}
        {founded_year && (
          <p>
            <strong>{t('organization.founded_year')}:</strong> {founded_year}
          </p>
        )}
        {estimated_num_employees && (
          <p>
            <strong>{t('organization.estimated_employees')}:</strong> {estimated_num_employees}
          </p>
        )}

        {/* Dirección */}
        {(street_address || city || state || country || postal_code) && (
          <p>
            <strong>{t('organization.address')}:</strong>{' '}
            <span className="inline-flex space-x-2 whitespace-nowrap">
              {street_address && <span>{street_address},</span>}
              {city && <span>{city},</span>}
              {state && <span>{state},</span>}
              {country && <span>{country},</span>}
              {postal_code && <span>{postal_code}</span>}
            </span>
          </p>
        )}

        {/* Alexa Ranking con tooltip */}
        {alexa_ranking && (
          <div className="relative inline-block">
            <p>
              <strong>{t('organization.alexa_ranking')}:</strong> {alexa_ranking}
              <span
                className="ml-2 inline-flex items-center text-gray-500 cursor-pointer"
                onMouseEnter={() => setShowTooltip(true)}
                onMouseLeave={() => setShowTooltip(false)}
                onClick={() => setShowTooltip(!showTooltip)}
              >
                <HelpCircle className="w-4 h-4" />
              </span>
            </p>

            {showTooltip && (
              <div className="absolute left-0 mt-2 w-64 p-3 bg-gray-700 text-white rounded-lg shadow-lg z-10 transition-opacity duration-200 opacity-100">
                {t('organization.alexa_ranking_tooltip')}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default OrganizationDetails;
